import { addDays, compareLocalDate, isLeapYear, parseLocalDate, weekdayOf, } from '../recurrence/localDate.js';
export const DEFAULT_HIGHLIGHT_OPTIONS = {
    birthdays: true,
    publicHolidays: true,
    schoolHolidays: true,
};
/**
 * Datum van de verjaardag in `year`. Regel (requirements "29 februari"): wie op 29 februari
 * jarig is, viert het in een niet-schrikkeljaar op 28 februari.
 */
export function birthdayInYear(birthDate, year) {
    const b = parseLocalDate(birthDate);
    const day = b.month === 2 && b.day === 29 && !isLeapYear(year) ? 28 : b.day;
    return `${String(year).padStart(4, '0')}-${pad(b.month)}-${pad(day)}`;
}
/** Leeftijd die iemand op `date` wordt of is (hele jaren). */
export function ageOn(birthDate, date) {
    const b = parseLocalDate(birthDate);
    const d = parseLocalDate(date);
    const hadBirthday = compareLocalDate(birthdayInYear(birthDate, d.year), date) <= 0;
    return d.year - b.year - (hadBirthday ? 0 : 1);
}
/** Paaszondag (anonieme gregoriaanse berekening, Meeus/Jones/Butcher). */
export function easterSunday(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return `${year}-${pad(month)}-${pad(day)}`;
}
/**
 * De set "Nederlandse feestdagen" (golf 2, oprichter #2: "Voeg de officiële Nederlandse
 * feestdagen ook bovenaan toe bij bijzondere dagen"): standaard AAN voor iedereen, zoals altijd;
 * per set (household.settings.family_layers.public_holidays) of per dag
 * (family_layers.public_holidays_off) uit te zetten. Geen gevoelig gegeven: geen toestemming nodig.
 * Volgorde = volgorde in Instellingen (eerst de officiële vrije dagen, dan de gezinsdagen).
 */
export const DUTCH_DAY_KEYS = [
    'nieuwjaarsdag',
    'goede_vrijdag',
    'pasen',
    'koningsdag',
    'bevrijdingsdag',
    'hemelvaartsdag',
    'pinksteren',
    'kerstmis',
    'moederdag',
    'vaderdag',
    'sinterklaas',
    'oudejaarsavond',
];
/** Canonieke lijst uitgezette Nederlandse dagen: alleen bekende sleutels, zonder dubbelen, in vaste volgorde. */
export function canonicalDutchKeysOff(keys) {
    if (!keys)
        return [];
    return DUTCH_DAY_KEYS.filter((k) => keys.indexOf(k) >= 0);
}
/** N-de zondag van een maand (Moederdag, Vaderdag). */
function nthSunday(year, month, n) {
    let date = `${year}-${pad(month)}-01`;
    while (weekdayOf(date) !== 'SU')
        date = addDays(date, 1);
    return addDays(date, 7 * (n - 1));
}
/**
 * Nederlandse feestdagen van `year` (officieel, zoals op de Rijksoverheid-lijst) plus een paar
 * gezinsdagen die kinderen herkennen. Koningsdag valt op 26 april als 27 april een zondag is.
 */
export function dutchDays(year) {
    const easter = easterSunday(year);
    const kingsDay = weekdayOf(`${year}-04-27`) === 'SU' ? `${year}-04-26` : `${year}-04-27`;
    const official = [
        [`${year}-01-01`, 'nieuwjaarsdag', 'Nieuwjaarsdag', 'party'],
        [addDays(easter, -2), 'goede_vrijdag', 'Goede Vrijdag', 'calendar'],
        [easter, 'pasen', 'Eerste Paasdag', 'breakfast'],
        [addDays(easter, 1), 'pasen', 'Tweede Paasdag', 'breakfast'],
        [kingsDay, 'koningsdag', 'Koningsdag', 'party'],
        [`${year}-05-05`, 'bevrijdingsdag', 'Bevrijdingsdag', 'party'],
        [addDays(easter, 39), 'hemelvaartsdag', 'Hemelvaartsdag', 'calendar'],
        [addDays(easter, 49), 'pinksteren', 'Eerste Pinksterdag', 'calendar'],
        [addDays(easter, 50), 'pinksteren', 'Tweede Pinksterdag', 'calendar'],
        [`${year}-12-25`, 'kerstmis', 'Eerste Kerstdag', 'gift'],
        [`${year}-12-26`, 'kerstmis', 'Tweede Kerstdag', 'gift'],
    ];
    const special = [
        [nthSunday(year, 5, 2), 'moederdag', 'Moederdag', 'gift'],
        [nthSunday(year, 6, 3), 'vaderdag', 'Vaderdag', 'gift'],
        [`${year}-12-05`, 'sinterklaas', 'Sinterklaasavond', 'gift'],
        [`${year}-12-31`, 'oudejaarsavond', 'Oudejaarsavond', 'party'],
    ];
    return [
        ...official.map(([date, key, name, icon]) => ({
            date,
            key,
            name,
            icon,
            kind: 'public_holiday',
        })),
        ...special.map(([date, key, name, icon]) => ({
            date,
            key,
            name,
            icon,
            kind: 'special_day',
        })),
    ].sort((x, y) => compareLocalDate(x.date, y.date));
}
/** Markeringen per datum (inclusief `from` en `to`), in vaste volgorde: verjaardag, feestdag, vakantie. */
export function computeHighlights(input) {
    const out = new Map();
    const push = (date, h) => {
        if (compareLocalDate(date, input.from) < 0 || compareLocalDate(date, input.to) > 0)
            return;
        const list = out.get(date);
        if (list)
            list.push(h);
        else
            out.set(date, [h]);
    };
    const fromYear = parseLocalDate(input.from).year;
    const toYear = parseLocalDate(input.to).year;
    if (input.options.birthdays) {
        for (const p of input.people) {
            for (let year = fromYear; year <= toYear; year += 1) {
                const date = birthdayInYear(p.birth_date, year);
                if (compareLocalDate(date, p.birth_date) < 0)
                    continue;
                const age = ageOn(p.birth_date, date);
                push(date, {
                    kind: 'birthday',
                    title: age > 0 ? `${p.name} wordt ${age}` : `${p.name} is jarig`,
                    icon: 'birthday',
                    member_id: p.id,
                    color_key: p.color_key,
                    age: age > 0 ? age : null,
                    day_index: 1,
                    day_count: 1,
                    household_label: p.household_label ?? null,
                });
            }
        }
    }
    if (input.options.publicHolidays) {
        const off = input.options.publicHolidayKeysOff ?? [];
        for (let year = fromYear; year <= toYear; year += 1) {
            for (const d of dutchDays(year)) {
                if (off.indexOf(d.key) >= 0)
                    continue;
                push(d.date, {
                    kind: d.kind,
                    title: d.name,
                    icon: d.icon,
                    member_id: null,
                    color_key: null,
                    age: null,
                    day_index: 1,
                    day_count: 1,
                    household_label: null,
                });
            }
        }
    }
    if (input.options.schoolHolidays) {
        for (const hol of input.schoolHolidays) {
            const count = daysBetween(hol.start_date, hol.end_date) + 1;
            for (let i = 0; i < count; i += 1) {
                push(addDays(hol.start_date, i), {
                    kind: 'school_holiday',
                    title: hol.name,
                    icon: 'holiday',
                    member_id: null,
                    color_key: null,
                    age: null,
                    day_index: i + 1,
                    day_count: count,
                    household_label: null,
                });
            }
        }
    }
    return out;
}
/** Eerstvolgende Nederlandse feestdagen vanaf `from` (voorvertoning in Instellingen), één per sleutel. */
export function previewDutchDays(from, n = 3, keysOff = []) {
    const y0 = parseLocalDate(from).year;
    const out = [];
    for (let y = y0; y <= y0 + 1 && out.length < n; y += 1) {
        for (const d of dutchDays(y)) {
            if (compareLocalDate(d.date, from) < 0 || keysOff.indexOf(d.key) >= 0)
                continue;
            if (out.some((x) => x.key === d.key))
                continue;
            out.push(d);
            if (out.length >= n)
                break;
        }
    }
    return out;
}
/** Eerstvolgende verjaardagen vanaf `today` (voor "Binnenkort jarig"). */
export function upcomingBirthdays(people, today, limit = 6) {
    const year = parseLocalDate(today).year;
    return people
        .map((person) => {
        let date = birthdayInYear(person.birth_date, year);
        if (compareLocalDate(date, today) < 0)
            date = birthdayInYear(person.birth_date, year + 1);
        return {
            person,
            date,
            age: ageOn(person.birth_date, date),
            in_days: daysBetween(today, date),
        };
    })
        .sort((a, b) => a.in_days - b.in_days || a.person.name.localeCompare(b.person.name))
        .slice(0, limit);
}
function daysBetween(a, b) {
    const pa = parseLocalDate(a);
    const pb = parseLocalDate(b);
    return Math.round((Date.UTC(pb.year, pb.month - 1, pb.day) - Date.UTC(pa.year, pa.month - 1, pa.day)) /
        86_400_000);
}
function pad(n) {
    return n < 10 ? `0${n}` : String(n);
}
//# sourceMappingURL=days.js.map